import { useAdversaries } from '../context/adversary-context'
import { AdversaryActionDialog } from './adversaries-action-dialog'
import { DeleteDialog } from '@/components/delete-dialog'
import { useTranslation } from 'react-i18next'
import { remove_case_adversary, get_adversaries, add_case_party } from '@/api/case'
import { remove_client } from '@/api/client'
import { toast } from '@/hooks/use-toast'
import { handleServerError } from '@/utils/handle-server-error'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { PaginatedSelect } from '@/components/paginated-select'
import { Button } from '@/components/ui/button'
import { Plus } from 'lucide-react'
import { useState, useEffect } from 'react'
import { CaseAdversary } from '@/api/case'

interface AdversariesDialogsProps {
    caseId?: number
    onUpdate?: () => void
}

export function AdversariesDialogs({ caseId, onUpdate }: AdversariesDialogsProps) {
    const { t } = useTranslation()
    const { open, setOpen, currentRow, setCurrentRow } = useAdversaries()
    const [showCreateForm, setShowCreateForm] = useState(false)

    // Reset local state when dialog closes
    useEffect(() => {
        if (open !== 'add') {
            setShowCreateForm(false)
        }
    }, [open])

    const handleDelete = async () => {
        if (!currentRow) return
        try {
            if (caseId) {
                await remove_case_adversary(currentRow.id)
            } else {
                // Global delete - use ID from normalized object (which is client ID)
                await remove_client(currentRow.id)
            }
            toast({
                title: t('adversary_removed', { name: currentRow.client.name }),
            })
            onUpdate?.()
            setOpen(null)
            setCurrentRow(null)
        } catch (error) {
            handleServerError(error)
        }
    }

    const fetchAdversariesOptions = async (page: number, search?: string) => {
        const list = await get_adversaries({
            ordering: null,
            page: (page - 1) * 30,
            page_size: 30,
            search: search ? search : null,
            client_type: null,
            id_type: null
        })
        const updated_list = list.results.map((v: CaseAdversary) => {
            return {
                id: v.client.id,
                name: v.client.idnumber ? `(${v.client.idnumber}) ${v.client.name}` : v.client.name
            }
        })
        return {
            items: updated_list,
            hasMore: list.next != null,
        }
    }

    const handleSelectExisting = (client: any) => {
        if (client && caseId) {
            add_case_party(caseId, { client: String(client.id), role: 'adversary' })
                .then(() => {
                    toast({
                        title: t('adversary_added_to_case', { name: client.name }),
                    });
                    onUpdate?.();
                    setOpen(null);
                })
                .catch((err) => {
                    handleServerError(err);
                });
        }
    }

    // Show Selection Dialog if: adding, has caseId, and NO create form active
    const showSelectionDialog = open === 'add' && !!caseId && !showCreateForm

    // Show Create Form if: adding, and (NO caseId OR create form active)
    const showCreateFormDialog = open === 'add' && (!caseId || showCreateForm)

    return (
        <>
            {/* Selection Dialog (only when adding to a specific case) */}
            {!!caseId && (
                <Dialog open={showSelectionDialog} onOpenChange={(val) => !val && setOpen(null)}>
                    <DialogContent className="sm:max-w-lg">
                        <DialogHeader className="text-right">
                            <DialogTitle>{t('add_adversary_to_case')}</DialogTitle>
                        </DialogHeader>

                        <Tabs defaultValue="existing" className="w-full">
                            <TabsList className="grid w-full grid-cols-2">
                                <TabsTrigger value="existing">{t('select_existing_adversary')}</TabsTrigger>
                                <TabsTrigger value="new">{t('create_new_adversary')}</TabsTrigger>
                            </TabsList>

                            <TabsContent value="existing" className="space-y-4 pt-4">
                                <PaginatedSelect
                                    value={undefined}
                                    placeholder={t('select_client')}
                                    fetchItems={fetchAdversariesOptions}
                                    onSelect={handleSelectExisting}
                                    triggerId="add-adversary-select-trigger"
                                    className="w-full"
                                />
                            </TabsContent>

                            <TabsContent value="new" className="pt-4">
                                <Button
                                    type="button"
                                    variant="outline"
                                    className="w-full"
                                    onClick={() => setShowCreateForm(true)}
                                >
                                    <Plus className="h-4 w-4 mr-2" />
                                    {t('create_new_adversary')}
                                </Button>
                            </TabsContent>
                        </Tabs>
                    </DialogContent>
                </Dialog>
            )}

            <AdversaryActionDialog
                caseId={caseId}
                open={showCreateFormDialog}
                onOpenChange={(val) => {
                    if (!val) setOpen(null)
                    // If we closed the create form but we were in "case linking mode", 
                    // we might want to go back to selection or close everything.
                    // Usually closing means "cancel everything".
                }}
                onSuccess={() => onUpdate?.()}
            />

            {currentRow && (
                <>
                    <AdversaryActionDialog
                        caseId={caseId}
                        open={open === 'edit'}
                        onOpenChange={() => setOpen(null)}
                        currentRow={currentRow}
                        onSuccess={() => onUpdate?.()}
                    />

                    <DeleteDialog
                        open={open === 'delete'}
                        onOpenChange={() => setOpen(null)}
                        deleteRow={handleDelete}
                        onDeleted={() => {
                            setCurrentRow(null);
                        }}
                        getDeleteName={() => currentRow.client.name}
                        deleteTitle={t('delete_adversary')}
                    />
                </>
            )}
        </>
    )
}
